import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useO5AwsDeployerV1EnvironmentQueryServiceEnvironmentGet } from '@interxfi/api-client/generated/hooks/o5-aws-deployer-v1-environment-query-service-environment-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { getShortenedId } from '@interxfi/design-system/components/format/id/shared';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { ReadonlyList } from '@interxfi/design-system/components/list/readonly/readonly-list';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import { Typography } from '@interxfi/design-system/components/typography';
import type { O5AwsDeployerV1EnvironmentState } from '@interxfi/types/generated/api';
import { O5EnvironmentAwsLinksTable } from '../../../../../../../features/o5/environment/o5-environment-aws-links-table';
import { O5EnvironmentCustomVariablesTable } from '../../../../../../../features/o5/environment/o5-environment-custom-variables-table';
import { O5EnvironmentEntityMenu } from '../../../../../../../features/o5/environment/o5-environment-entity-menu';
import { O5EnvironmentEventSummary } from '../../../../../../../features/o5/environment/o5-environment-event';
import { getO5EnvironmentStateListItems } from '../../../../../../../features/o5/environment/o5-environment-list-items';
import { O5EnvironmentNamedIamPoliciesTable } from '../../../../../../../features/o5/environment/o5-environment-named-iam-policies-table';
import { getO5EnvironmentDetailPath } from '../../../../../../../features/o5/environment/o5-environment-routing';
import { O5EnvironmentStatusPill } from '../../../../../../../features/o5/environment/o5-environment-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../util/routing/meta';

function EnvironmentBreadcrumb({ environmentId }: { environmentId: string }) {
  const { data, error, isPending } = useO5AwsDeployerV1EnvironmentQueryServiceEnvironmentGet({ environmentId });
  const showPending = Boolean(isPending || error);

  if (showPending) {
    return (
      <span>
        <Skeleton charCount={8} />
      </span>
    );
  }

  return data?.environment?.data?.config?.fullName || `Environment ${getShortenedId(environmentId)}`;
}

export const Route = createFileRoute('/_app/_o5/_o5-required/tech-ops/environment/_environment/$environmentId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `o5-environment-${params.environmentId}`,
        label: <EnvironmentBreadcrumb environmentId={params.environmentId} />,
        to: getO5EnvironmentDetailPath(params.environmentId),
        suffix: (
          <CopyButton
            content={params.environmentId}
            label="Copy Environment ID"
            tooltip={{ copy: params.environmentId, copied: 'Copied Environment ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Environment'),
  component: O5EnvironmentDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load environment', 'view this environment');

function getListItems(data: O5AwsDeployerV1EnvironmentState | undefined) {
  const items = getO5EnvironmentStateListItems(data);

  return {
    left: [
      items.data.config.fullName,
      items.data.config.clusterName,
      items.data.config.eventSourceAliases,
      items.data.config.corsOrigins,
      items.data.config.trustJwks,
    ] as const satisfies ReadonlyListItem[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.environmentId, items.clusterId] as const satisfies ReadonlyListItem[],
    aws: [...('aws' in items.data.config.provider ? [items.data.config.provider.aws.value.hostHeader] : [])] as const satisfies ReadonlyListItem[],
  } as const;
}

function O5EnvironmentDetail() {
  const { environmentId } = Route.useParams();
  const request = { environmentId } as const;
  const { data, error, isPending } = useO5AwsDeployerV1EnvironmentQueryServiceEnvironmentGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.environment), [data?.environment]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <O5EnvironmentStatusPill isPending={showPending} size="large" status={data?.environment?.status} />
          </HorizontalStack>
          <O5EnvironmentEntityMenu environmentId={environmentId} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <O5EnvironmentEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Variables</Typography.Level3>
          <O5EnvironmentCustomVariablesTable showSkeleton={showPending} variables={data?.environment?.data?.config?.vars || []} />
        </VerticalStack>
      </PageLayout.Section>

      {data?.environment?.data?.config?.provider?.aws ? (
        <PageLayout.Section>
          <VerticalStack gap={7}>
            <VerticalStack gap={4}>
              <Typography.Level3>AWS</Typography.Level3>
              <ReadonlyList items={listItems.aws} />
            </VerticalStack>

            <VerticalStack gap={4}>
              <Typography.Level2>Links</Typography.Level2>
              <O5EnvironmentAwsLinksTable links={data.environment.data.config.provider.aws.environmentLinks || []} />
            </VerticalStack>

            <VerticalStack gap={4}>
              <Typography.Level2>IAM Policies</Typography.Level2>
              <O5EnvironmentNamedIamPoliciesTable policies={data.environment.data.config.provider.aws.iamPolicies || []} />
            </VerticalStack>
          </VerticalStack>
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
